'use strict';

const DEFAULT_API_BASE = 'https://api.near.services/mailinglist/api';
const DEFAULT_LIST_ID = 'nearservices';

class NearMailinglist extends HTMLElement {
  static get observedAttributes() {
    return ['api-base', 'list-id', 'service-slug', 'topics', 'lang', 'privacy-policy'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = { status: 'idle', message: '', privacyAccepted: false };
    this.onSubmit = this.onSubmit.bind(this);
    this.onLocationChange = this.onLocationChange.bind(this);
    this.onPrivacyChange = this.onPrivacyChange.bind(this);
  }

  connectedCallback() {
    this.render();
    this.setupLocationListeners();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onLocationChange);
    window.removeEventListener('hashchange', this.onLocationChange);
    window.removeEventListener('near-ml-locationchange', this.onLocationChange);
  }

  attributeChangedCallback() {
    this.render();
  }

  get apiBase() {
    return this.getAttribute('api-base') || DEFAULT_API_BASE;
  }

  get listId() {
    return this.getAttribute('list-id') || DEFAULT_LIST_ID;
  }

  get serviceSlug() {
    return this.getAttribute('service-slug') || '';
  }

  get topics() {
    const raw = this.getAttribute('topics');
    if (!raw) return [];
    const value = raw.trim();
    if (!value) return [];

    if (value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((topic) => String(topic).trim()).filter(Boolean);
        }
      } catch (err) {
        // Fallback to CSV parsing when JSON is invalid.
      }
    }

    return value.split(',').map((topic) => topic.trim()).filter(Boolean);
  }

  get lang() {
    const own = this.getAttribute('lang');
    if (own && own !== 'auto') return own;
    const docLang = document.documentElement && document.documentElement.getAttribute('lang');
    return docLang || 'es';
  }

  get privacyPolicy() {
    return this.getAttribute('privacy-policy') || '';
  }

  setState(next) {
    this.state = { ...this.state, ...next };
    this.render();
  }

  isValidEmail(value) {
    if (!value) return false;
    if (value.length > 254) return false;
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.privacyPolicy && !this.state.privacyAccepted) {
      this.setState({
        status: 'error',
        message: this.lang === 'en'
          ? 'Please accept the privacy policy to continue.'
          : 'Acepta la política de privacidad para continuar.',
      });
      return;
    }
    const emailInput = this.shadowRoot.querySelector('input[name="email"]');
    const honeyInput = this.shadowRoot.querySelector('input[name="company"]');
    const email = emailInput ? emailInput.value.trim() : '';

    if (!this.isValidEmail(email)) {
      this.setState({ status: 'error', message: this.lang === 'en' ? 'Invalid email.' : 'Email inválido.' });
      return;
    }

    this.setState({ status: 'busy', message: '' });

    try {
      const resp = await fetch(`${this.apiBase}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          listId: this.listId || undefined,
          serviceSlug: this.serviceSlug || undefined,
          topics: this.topics.length ? this.topics : undefined,
          lang: this.lang,
          honey: honeyInput ? honeyInput.value : '',
          sourceUrl: window.location.href,
          source: {
            formId: this.getAttribute('form-id') || 'near-mailinglist',
          },
        }),
      });

      if (!resp.ok) {
        throw new Error('request_failed');
      }

      this.dispatchEvent(new CustomEvent('subscribed', { detail: { email } }));
      const msg = this.lang === 'en'
        ? 'We sent a confirmation message.'
        : 'Te enviamos un correo de confirmación.';
      this.setState({ status: 'ok', message: msg });
      if (emailInput) emailInput.value = '';
    } catch (err) {
      this.dispatchEvent(new CustomEvent('error', { detail: { error: err } }));
      const msg = this.lang === 'en'
        ? 'Something went wrong. Try again.'
        : 'Ocurrió un error. Intentá nuevamente.';
      this.setState({ status: 'error', message: msg });
    }
  }

  render() {
    const { status, message } = this.state;
    const disabled = status === 'busy';
    const buttonLabel = this.lang === 'en' ? 'Subscribe' : 'Suscribirme';
    const placeholder = this.lang === 'en' ? 'you@email.com' : 'tu@email.com';
    const helperAttr = this.getAttribute('helper') || '';
    const showHelper = Boolean(helperAttr);
    const modal = this.getModalMessage();
    const policyUrl = this.privacyPolicy;
    const policyRequired = Boolean(policyUrl);
    const policyChecked = Boolean(this.state.privacyAccepted);
    const submitDisabled = disabled || (policyRequired && !policyChecked);
    const policyLabel = this.lang === 'en' ? 'Read and accept our' : 'Lee y acepta nuestra';
    const policyLink = this.lang === 'en' ? 'privacy policy' : 'política de privacidad';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: var(--pico-font-family, system-ui);
          color: var(--pico-color, #111);
          max-width: 500px;
          margin: 2rem auto;
        }
        .card {
          padding: 1rem;
          border: 1px solid var(--pico-muted-border-color, #ddd);
          border-radius: var(--pico-border-radius, 8px);
          background: color-mix(in srgb, var(--pico-card-background-color, #ffffff) 30%, transparent);
        }
        form {
          display: grid;
          gap: 0.75rem;
        }
        .row {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 0.5rem;
        }
        input[type="email"] {
          padding: 0.6rem 0.75rem;
          border-radius: var(--pico-border-radius, 8px);
          border: 1px solid var(--pico-muted-border-color, #ddd);
          font-size: 1rem;
        }
        button {
          padding: 0.6rem 1rem;
          border-radius: var(--pico-border-radius, 8px);
          border: 1px solid var(--pico-primary-border, #0d6efd);
          background: var(--pico-primary, #0d6efd);
          color: var(--pico-primary-inverse, #fff);
          font-weight: 600;
          cursor: pointer;
        }
        button[disabled] {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .meta {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.75rem;
        }
        .helper {
          color: var(--pico-muted-color, #666);
          font-size: 0.9rem;
        }
        .policy {
          color: var(--pico-muted-color, #666);
          font-size: 0.9rem;
          text-align: right;
        }
        .policy label {
          display: flex;
          flex-direction: row-reverse;
          gap: 0.5rem;
          align-items: flex-start;
        }
        .policy a {
          color: inherit;
        }
        .message {
          font-size: 0.9rem;
        }
        .message.ok { color: var(--pico-ins-color, #1a7f37); }
        .message.error { color: var(--pico-del-color, #b91c1c); }
        .honeypot {
          position: absolute;
          left: -9999px;
          top: auto;
          width: 1px;
          height: 1px;
          overflow: hidden;
        }
        @media (max-width: 600px) {
          .row { grid-template-columns: 1fr; }
          button { width: 100%; }
        }
        dialog {
          border: none;
          border-radius: calc(var(--pico-border-radius, 8px) * 1.1);
          padding: 1.25rem 1.5rem;
          max-width: 440px;
          width: min(92vw, 440px);
          background: var(--pico-card-background-color, #ffffff);
          color: var(--pico-color, #111);
          box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          animation: modal-pop 220ms ease-out;
        }
        .modal-overlay {
          position: fixed;
          inset: 0;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 2147483646;
        }
        dialog {
          position: fixed;
          inset: 50% auto auto 50%;
          transform: translate(-50%, -50%);
          z-index: 2147483647;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 1rem;
        }
        .modal-actions button {
          width: auto;
        }
        .modal-title {
          font-weight: 700;
          font-size: 1.1rem;
          margin-bottom: 0.5rem;
        }
        .modal-body {
          color: var(--pico-muted-color, #666);
        }
        .modal-actions button {
          background: var(--pico-primary, #0d6efd);
          color: var(--pico-primary-inverse, #fff);
          border: 1px solid var(--pico-primary-border, #0d6efd);
        }
        @keyframes modal-pop {
          from { transform: translateY(10px) scale(0.98); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
      </style>
      <div class="card">
        <form novalidate>
          <div class="row">
            <input type="email" name="email" placeholder="${placeholder}" required />
            <button type="submit" ${submitDisabled ? 'disabled' : ''}>${buttonLabel}</button>
          </div>
          <input class="honeypot" type="text" name="company" tabindex="-1" autocomplete="off" />
          ${(showHelper || policyRequired) ? `
            <div class="meta">
              ${showHelper ? `<div class="helper">${helperAttr}</div>` : '<div></div>'}
              ${policyRequired ? `
                <div class="policy">
                  <label>
                    <input type="checkbox" name="privacy-policy" ${policyChecked ? 'checked' : ''} />
                    <span>${policyLabel} <a href="${policyUrl}" target="_blank" rel="noopener">${policyLink}</a>.</span>
                  </label>
                </div>
              ` : ''}
            </div>
          ` : ''}
          ${message ? `<div class="message ${status === 'error' ? 'error' : 'ok'}">${message}</div>` : ''}
        </form>
      </div>
      ${modal ? '<div class="modal-overlay" data-close></div>' : ''}
      <dialog ${modal ? 'open' : ''}>
        ${modal ? `<div class="modal-title">${this.lang === 'en' ? 'Subscription update' : 'Actualización de suscripción'}</div>` : ''}
        ${modal ? `<div class="modal-body">${modal}</div>` : ''}
        <div class="modal-actions">
          <button type="button" data-close>OK</button>
        </div>
      </dialog>
    `;

    const form = this.shadowRoot.querySelector('form');
    if (form) {
      form.removeEventListener('submit', this.onSubmit);
      form.addEventListener('submit', this.onSubmit);
    }
    const checkbox = this.shadowRoot.querySelector('input[name="privacy-policy"]');
    if (checkbox) {
      checkbox.removeEventListener('change', this.onPrivacyChange);
      checkbox.addEventListener('change', this.onPrivacyChange);
    }
    const closeTargets = this.shadowRoot.querySelectorAll('[data-close]');
    const dialog = this.shadowRoot.querySelector('dialog');
    closeTargets.forEach((el) => {
      el.addEventListener('click', () => {
        if (dialog) dialog.close();
        this.cleanModalQuery();
      });
    });
    if (dialog && modal && typeof dialog.showModal === 'function' && !dialog.open) {
      dialog.showModal();
    }
    if (dialog) {
      dialog.addEventListener('close', () => {
        this.setState({ status: this.state.status, message: this.state.message });
      }, { once: true });
    }
  }

  onLocationChange() {
    this.render();
  }

  onPrivacyChange(event) {
    const checked = Boolean(event && event.target && event.target.checked);
    this.setState({ privacyAccepted: checked });
  }

  setupLocationListeners() {
    if (!window.__nearMlHistoryPatched) {
      const wrap = (method) => {
        const original = history[method];
        history[method] = function () {
          const result = original.apply(this, arguments);
          window.dispatchEvent(new Event('near-ml-locationchange'));
          return result;
        };
      };
      wrap('pushState');
      wrap('replaceState');
      window.__nearMlHistoryPatched = true;
    }
    window.addEventListener('popstate', this.onLocationChange);
    window.addEventListener('hashchange', this.onLocationChange);
    window.addEventListener('near-ml-locationchange', this.onLocationChange);
  }

  getModalMessage() {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('ml_status');
    if (!status) return '';
    const error = params.get('ml_error');
    const es = this.lang !== 'en';
    if (status === 'confirmed') {
      return es ? 'Tu suscripción fue confirmada.' : 'Your subscription was confirmed.';
    }
    if (status === 'unsubscribed') {
      return es ? 'Tu suscripción fue cancelada.' : 'You have been unsubscribed.';
    }
    if (status === 'error') {
      if (error === 'invalid_token') {
        return es ? 'El enlace no es válido o ya expiró.' : 'The link is invalid or has expired.';
      }
      return es ? 'Ocurrió un error. Intentá nuevamente.' : 'Something went wrong. Please try again.';
    }
    return '';
  }

  cleanModalQuery() {
    const url = new URL(window.location.href);
    url.searchParams.delete('ml_status');
    url.searchParams.delete('ml_error');
    window.history.replaceState({}, document.title, url.toString());
  }
}

customElements.define('near-mailinglist', NearMailinglist);
